/**
 * Custom hook for tracking page views in Google Analytics 4 (GA4)
 * 
 * This hook listens to location changes in React Router and sends a page_view event
 * to GA4 whenever the URL changes. This is necessary for SPAs because GA4's auto
 * page view tracking only fires on initial page load.
 * 
 * The hook also ensures that GA4 can properly detect site search activity through
 * Enhanced Measurement by sending the correct page_location and page_path parameters.
 * 
 * Usage:
 *   import { usePageTracking } from './util/usePageTracking';
 *   
 *   function MyComponent() {
 *     usePageTracking();
 *     return <div>...</div>;
 *   }
 * 
 * @module usePageTracking
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook that sends a page_view event to GA4 whenever the location changes
 * 
 * This ensures that:
 * 1. GA4 receives page_view events for client-side route changes
 * 2. The page_location and page_path parameters are correctly updated
 * 3. GA4's Enhanced Measurement can detect query parameters like 'keywords' for site search
 * 4. Search results are properly tracked as view_search_results events
 * 
 * @returns {void}
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if gtag is available (GA4 script loaded)
    if (typeof window !== 'undefined' && window.gtag) {
      // Send page_view event to GA4 with current location data
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
      });

      // Log in development mode for debugging
      if (process.env.NODE_ENV === 'development') {
        console.debug('[GA4 Page Tracking]', {
          page_location: window.location.href,
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
        });
      }
    }
  }, [location]);
};

export default usePageTracking;
